// Public, unauthenticated view for a recipe share link (see
// supabase/migrations/0013_recipe_share_links_and_shared_by.sql and
// RecipeSharing.jsx's "+ Create share link"). Deliberately its own small
// read-only renderer rather than reusing the recipe page, which assumes a
// signed-in app session (week plan, favorites, editing, the sharing panel
// itself), none of which apply to a visitor who just clicked a link.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { NON_NUMERIC_UNITS, RECIPE_TYPE_LABELS, COLORS, FONT_SIZE, SPACE, RADIUS, LAYOUT } from "../lib/constants";
import { formatQuantity, parseQuantity } from "../lib/utils";
import { buildGroupedRows } from "../lib/recipeUtils";
import { DietBadges, SeasonBadges, CookingMethodBadges } from "../components/Badges";
import { getRecipeByShareToken } from "../lib/db";

export function SharedRecipePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, recipe: null, error: false });
  const { paper, card, ink, soft, accent, line } = COLORS;

  useEffect(() => {
    let cancelled = false;
    getRecipeByShareToken(token)
      .then((recipe) => {
        if (!cancelled) setState({ loading: false, recipe, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, recipe: null, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const wrapperStyle = { background: paper, color: ink, fontFamily: "var(--font-serif)", minHeight: "100vh", padding: SPACE.x4, boxSizing: "border-box" };

  if (state.loading) {
    return (
      <div style={{ ...wrapperStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: soft }}>Loading recipe…</span>
      </div>
    );
  }

  if (state.error || !state.recipe) {
    return (
      <div style={{ ...wrapperStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: soft }}>This link isn't valid — it may have been revoked.</span>
      </div>
    );
  }

  const recipe = state.recipe;
  const steps = recipe.instructions || [];
  let stepCounter = 0;
  const numberedSteps = steps.map((item) => {
    if (item.type === "step") stepCounter += 1;
    return { ...item, displayNum: item.type === "step" ? stepCounter : null };
  });

  return (
    <div style={wrapperStyle}>
      <div style={{ maxWidth: 900, margin: "0 auto", background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xxl, padding: SPACE.x4 }}>
        <div className="mp-sans" style={{ fontSize: FONT_SIZE.xs, color: soft, marginBottom: SPACE.lg }}>
          Shared from The Weekly Kitchen
        </div>
        <h1 style={{ fontSize: FONT_SIZE.h1, fontWeight: 700, margin: "0 0 var(--space-md)" }}>{recipe.title || "Untitled recipe"}</h1>
        <div className="mp-sans" style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm, fontSize: FONT_SIZE.sm, marginBottom: SPACE.xxl }}>
          {recipe.cuisine && <span style={{ background: COLORS.amberTintDeep, color: COLORS.amberInkDeep, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.pill }}>{recipe.cuisine}</span>}
          <span style={{ background: COLORS.stoneTint, color: ink, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.pill }}>{RECIPE_TYPE_LABELS[recipe.recipeType] || recipe.recipeType}</span>
          <DietBadges recipe={recipe} colors={COLORS} />
          <SeasonBadges recipe={recipe} />
          <CookingMethodBadges recipe={recipe} />
        </div>
        {(recipe.source || recipe.sourceUrl) && (
          <div className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, fontStyle: "italic", marginBottom: SPACE.xxl, overflowWrap: "anywhere" }}>
            Source:{" "}
            {recipe.sourceUrl ? (
              <a href={recipe.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {recipe.source || recipe.sourceUrl.replace(/^https?:\/\/(www\.)?/i, "").split("/")[0]}
              </a>
            ) : (
              recipe.source
            )}
          </div>
        )}
        <div className="mp-sans" style={{ display: "flex", gap: SPACE.x4, fontSize: FONT_SIZE.base, color: soft, marginBottom: SPACE.x3, flexWrap: "wrap" }}>
          <span>{recipe.activeTime}m active</span>
          <span>{recipe.cookTime}m cook</span>
          <span>{recipe.recipeType === "preserves" || recipe.recipeType === "medicinal" ? `Yields ${recipe.yield || "—"}` : `Serves ${recipe.servings}`}</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: SPACE.x4, alignItems: "start" }}>
          <div>
            <h3 className="mp-sans" style={{ fontSize: FONT_SIZE.base, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: accent, margin: "0 0 var(--space-lg)" }}>
              Ingredients
            </h3>
            <ul className="mp-sans" style={{ margin: 0, padding: 0, listStyle: "none", fontSize: FONT_SIZE.md, display: "flex", flexDirection: "column", gap: SPACE.sm }}>
              {buildGroupedRows(recipe.ingredients || []).map((row) =>
                row.kind === "header" ? (
                  <li key={row.key} style={{ fontWeight: 700, fontSize: FONT_SIZE.sm, color: accent, textTransform: "uppercase", letterSpacing: "0.03em", marginTop: SPACE.sm, listStyle: "none" }}>
                    {row.label}
                  </li>
                ) : row.item.type === "note" ? (
                  <li key={row.key} style={{ borderBottom: `1px dashed ${line}`, paddingBottom: SPACE.sm, marginLeft: row.item.component ? SPACE.xl2 : 0, fontStyle: "italic", color: soft }}>
                    {row.item.name}
                  </li>
                ) : (
                  <li key={row.key} style={{ borderBottom: `1px dashed ${line}`, paddingBottom: SPACE.sm, marginLeft: row.item.component ? SPACE.xl2 : 0 }}>
                    {NON_NUMERIC_UNITS.includes(row.item.unit) ? (
                      <>
                        {row.item.name}
                        {row.item.prep ? `, ${row.item.prep}` : ""} — {row.item.unit}
                      </>
                    ) : (
                      <>
                        {formatQuantity(parseQuantity(row.item.quantity))}
                        {row.item.unit ? ` ${row.item.unit}` : ""} {row.item.name}
                        {row.item.prep ? `, ${row.item.prep}` : ""}
                      </>
                    )}
                  </li>
                )
              )}
            </ul>
          </div>
          <div>
            <h3 className="mp-sans" style={{ fontSize: FONT_SIZE.base, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: accent, margin: "0 0 var(--space-lg)" }}>
              Instructions
            </h3>
            <ol className="mp-sans" style={{ margin: 0, padding: 0, listStyle: "none", fontSize: FONT_SIZE.md, display: "flex", flexDirection: "column", gap: SPACE.xl }}>
              {buildGroupedRows(numberedSteps).map((row) =>
                row.kind === "header" ? (
                  <li key={row.key} style={{ fontWeight: 700, fontSize: FONT_SIZE.sm, color: accent, textTransform: "uppercase", letterSpacing: "0.03em", marginTop: SPACE.xs, listStyle: "none" }}>
                    {row.label}
                  </li>
                ) : (
                  <li key={row.key} style={{ display: "flex", gap: SPACE.lg, marginLeft: row.item.component ? LAYOUT.componentIndent : 0 }}>
                    {row.item.type === "step" ? (
                      <span style={{ fontWeight: 700, color: accent, flexShrink: 0 }}>{row.item.displayNum}.</span>
                    ) : (
                      <span style={{ color: COLORS.amberInk, flexShrink: 0, fontSize: FONT_SIZE.sm, paddingTop: SPACE.hair }}>note</span>
                    )}
                    <span style={{ fontStyle: row.item.type === "note" ? "italic" : "normal", color: row.item.type === "note" ? soft : ink }}>{row.item.text}</span>
                  </li>
                )
              )}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
